import React, { useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Grid, GridItem } from '../../grid/Grid.jsx'
import { constants } from './constants.js'
import { getLink, getLinkFromRef, getMargins } from '../../../utility/index.js'
import { BrManageContentButton } from '@bloomreach/react-sdk'
import './heroBanner.css'
import ButtonLinkCtaList from '../../link/buttonLinkCtaList.jsx'
import DOMPurify from 'dompurify'

export const BannerConnect = ({ page, component }) => {
  // process page and compoenent parameters here and pass the to Banner
  const _models = component.getModels()
  const { document: _documentRef } = _models
  const document = _documentRef && page.getContent(_documentRef)
  const data = document?.getData() || {}
  const imageUrl = page.getContent(data?.image?.$ref)?.getOriginal()?.getUrl()
  const mobileImageUrl = page
    .getContent(data?.mobileImage?.image?.$ref)
    ?.getOriginal()
    ?.getUrl()
  const bgImageUrl = page
    .getContent(data?.bgImage?.image?.$ref)
    ?.getOriginal()
    ?.getUrl()
  const params = component?.getParameters()

  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mediaQuery = matchMedia('(max-width: 768px)')
    setIsMobile(mediaQuery.matches)
    const handleChange = (event) => setIsMobile(event.matches)
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const {
    angleBorder,
    angleBorderColor,
    angleType,
    backgroundColor,
    columnCount,
    contentAlignment,
    eyebrowColor,
    eyebrowFont,
    secondaryHeadingColor,
    textColor,
    verticalPadding,
    width,
    horizontalCrop,
    verticalCrop,
    marginTop,
    marginBottom,
  } = params

  const componentProps = {
    angleBorder,
    angleBorderColor,
    angleType,
    backgroundColor,
    columnCount,
    contentAlignment,
    eyebrowColor,
    secondaryHeadingColor,
    textColor,
    verticalPadding,
    width,
    horizontalCrop,
    verticalCrop,
    marginBottom,
    marginTop,
  }
  const documentProps = {
    bgImageUrl: bgImageUrl,
    eyebrowFont: eyebrowFont,
    eyebrowText: data?.eyebrowText,
    imageUrl: imageUrl,
    mobileImageUrl: mobileImageUrl,
    primaryHeading:
      data?.title && typeof data?.title === 'object'
        ? data?.title?.value
        : data?.title || '',
    secondaryHeading: data?.subtitle,
    linkList: data?.links
      ?.filter((l) => l.link && l.link.length > 0)
      ?.map((l) => getLink(getLinkFromRef(l, page), null)),
  }

  const args = {
    ...componentProps,
    ...documentProps,
    isMobile: isMobile,
  }
  return (
    <div className="heroBanner u-posRelative">
      {document && <BrManageContentButton content={document} />}
      <HeroBanner {...args} />
    </div>
  )
}
export const BannerCutout = ({
  columnCount,
  angleBorder,
  angleType,
  angleBorderColor,
  bgClass,
  imageUrl,
  horizontalCrop,
  verticalCrop,
  isMobile,
  mobileImageUrl,
}) => {
  const gridSizeImage = {
    whole: 'u-hidden',
    fourths: 'u-size1of4',
    thirds: 'u-size1of3',
    halves: 'u-size1of2',
  }

  return (
    <div
      id="bannerCutout"
      className={`${gridSizeImage[columnCount]} u-posAbsolute u-sm-posRelative ${angleBorder} ${angleType} u-sm-sizeFull`}
    >
      <div className={`Shaddow2 ${bgClass} ${angleBorderColor}`}></div>
      <div className={`Shaddow1 ${bgClass} ${angleBorderColor}`}></div>
      <div className={`Clip ${bgClass}`}></div>
      {imageUrl && (
        <div
          id="bannerImage"
          className={`background-x-${horizontalCrop} background-y-${verticalCrop}`}
          style={{
            backgroundImage: `url("${isMobile ? mobileImageUrl || imageUrl : imageUrl}")`,
          }}
        ></div>
      )}
    </div>
  )
}

export const HeroBanner = ({
  backgroundColor = 'dark',
  imageUrl = '',
  bgImageUrl = '',
  mobileImageUrl = '',
  columnCount = 'thirds',
  textColor = 'white',
  primaryHeading = '',
  secondaryHeading = '',
  secondaryHeadingColor = 'inherit',
  eyebrowColor = 'white',
  eyebrowText = '',
  eyebrowFont = 'Publico',
  verticalPadding = 8,
  angleType = 'content',
  angleBorder = 'double',
  linkList = [],
  angleBorderColor = 'white',
  width = 'full',
  contentAlignment = '',
  isMobile = false,
  horizontalCrop = 'center',
  verticalCrop = 'top',
  marginTop = 'No Spacing',
  marginBottom = 'No Spacing',
}) => {
  const bgClass = constants.backgroundColors[backgroundColor] || ''
  const textColorClass =
    textColor === 'black' ? 'u-textColorBlack' : 'u-textColorWhite'
  const eyebrowFontClass = eyebrowFont === 'Roboto' ? 'roboto-font' : ''
  const eyebrowColorClass = constants.textColors[eyebrowColor]
  const widthClass = width === 'inset' ? 'u-sizeConstrained' : ''
  const contentAlignmentClass =
    contentAlignment === 'center' ? 'u-flexAlignItemsCenter' : ''

  const gridSizeContent = {
    whole: 'u-sizeFull',
    fourths: 'u-size3of4',
    thirds: 'u-size2of3',
    halves: 'u-size1of2',
  }

  const Cutout = (
    <BannerCutout
      columnCount={columnCount}
      angleBorder={angleBorder}
      angleType={angleType}
      angleBorderColor={angleBorderColor}
      bgClass={bgClass}
      imageUrl={imageUrl}
      horizontalCrop={horizontalCrop}
      verticalCrop={verticalCrop}
      isMobile={isMobile}
      mobileImageUrl={mobileImageUrl}
    />
  )

  return (
    <div
      className={`Banner ${bgClass} ${widthClass} ${width === 'inset' && 'u-paddingRight0'}`}
    >
      {width !== 'inset' && Cutout}
      <div
        className={`u-posRelative ${getMargins(
          marginTop,
          marginBottom
        )} u-overflowHidden u-flex u-sm-flexWrap u-sm-flexColReverse ${width !== 'inset' && 'u-sizeConstrained'}`}
      >
        <div
          className={`content u-posRelative ${gridSizeContent[columnCount]} u-sm-sizeFull`}
          style={{
            backgroundImage: bgImageUrl ? `url("${bgImageUrl}")` : 'none',
          }}
        >
          <Grid>
            <GridItem
              className={`u-paddingVert${verticalPadding}gu  u-flex u-flexCol  ${contentAlignmentClass} u-flexJustifyCenter  u-md-size2of3 u-sm-sizeFull ${textColorClass}`}
              columnCount="1"
              gridSize="1"
            >
              {eyebrowText && (
                <div
                  className={`eyebrow u-textSizeMsrp1 u-sm-textSizeMsr ${eyebrowFontClass} ${eyebrowColorClass}`}
                >
                  {eyebrowText}
                </div>
              )}
              {primaryHeading && (
                <div
                  className="primaryHeading"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(primaryHeading),
                  }}
                />
              )}
              {secondaryHeading && (
                <div
                  className={`secondaryHeading u-textSizeMsrp2 u-sm-textSizeMsrp1 u-paddingTop4gu ${secondaryHeadingColor}`}
                >
                  {secondaryHeading}
                </div>
              )}

              {linkList && linkList.length > 0 && (
                <div className="u-marginTop6gu">
                  <ButtonLinkCtaList linkList={linkList} />
                </div>
              )}
            </GridItem>
          </Grid>
        </div>
        {width == 'inset' && Cutout}
      </div>
    </div>
  )
}

function BannerErrorBoundary(props) {
  return (
    <ErrorBoundary
      fallback={<div></div>}
      onError={(error, info) => {
        console.log('BannerErrorBoundary error:', error, info)
      }}
    >
      <BannerConnect {...props}></BannerConnect>
    </ErrorBoundary>
  )
}

export default BannerErrorBoundary
